import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { BulkUpdateResultInfoInterface } from '../+external-interfaces/bulk-update-result-info';
import {
  EduContentInterface,
  EvaluationScoreInterface,
  EvaluationScoreListInterface,
  EvaluationScoreListOptionsInterface,
  EvaluationScoreListSubjectInterface,
  PrintSize,
  TaskClassGroupInterface,
  TaskEduContentInterface,
  TaskGoalYearLevelInterface,
  TaskGroupInterface,
  TaskInstanceInterface,
  TaskInterface,
  TaskStudentInterface,
} from '../+models';
import { FeedbackTriggeringAction } from '../+state/effect-feedback';

export const TASK_SERVICE_TOKEN = new InjectionToken<TaskServiceInterface>('TaskService');

export interface TaskAssigneesInterface {
  taskClassGroups?: TaskClassGroupInterface[];
  taskGroups?: TaskGroupInterface[];
  taskStudents?: TaskStudentInterface[];
}
export interface TaskAssigneeIdsInterface {
  classGroupIds?: number[];
  groupIds?: number[];
  studentIds?: number[];
}

export interface CreateEvaluationTaskConfigInterface {
  eduContentTOCId: number; // used to determine which evaluation is linked
  date: Date; // used to determine start/end date of task and assignees
  subjectIds: number[];
  scoreListOptions?: EvaluationScoreListOptionsInterface;
}

export interface WithAssigneesInterface {
  taskClassGroups: TaskClassGroupInterface[];
  taskGroups: TaskGroupInterface[];
  taskStudents: TaskStudentInterface[];
}
export interface CreateEvaluationTaskResponseInterface extends WithAssigneesInterface {
  task: TaskInterface;
  evaluationScoreListSubjects: EvaluationScoreListSubjectInterface[];
  evaluationScoreList: EvaluationScoreListInterface;
  taskEduContents: TaskEduContentInterface[];
  eduContents?: EduContentInterface[];
}

export interface CreateAdaptiveTaskResponseInterface extends WithAssigneesInterface {
  task: TaskInterface;
  evaluationScoreListSubjects: EvaluationScoreListSubjectInterface[];
  evaluationScoreList: EvaluationScoreListInterface;
}
export interface CreateEvaluationAndAdaptiveTaskResponseInterface {
  evaluation: CreateEvaluationTaskResponseInterface;
  adaptive: CreateAdaptiveTaskResponseInterface;
}

export interface UpdateEvaluationScoreListSubjectResponseInterface {
  taskEduContents?: TaskEduContentInterface[]; // evaluation task
  eduContents?: EduContentInterface[]; // evaluation task
  evaluationScoreListSubjects: EvaluationScoreListSubjectInterface[]; // both evaluation and adaptive task
  evaluationScores?: EvaluationScoreInterface[]; // adaptive task
  taskGoalYearLevels?: TaskGoalYearLevelInterface[]; // adaptive task
}

export interface TaskServiceInterface {
  getAllForUser(userId: number): Observable<TaskInterface[]>;
  linkEduContent(taskId: number, eduContentId: number): Observable<TaskEduContentInterface>;

  updateTasks(
    userId: number,
    update: Partial<TaskInterface>[],
    studentIds?: number[]
  ): Observable<UpdateTaskResultInterface>;

  createTask(userId: number, task: TaskInterface): Observable<TaskInterface>;

  duplicateTask(taskId: number): Observable<TaskInterface>;

  createEvaluationTask(
    task: TaskInterface,
    evaluationId: number,
    evaluationSubjectIds: number[],
    assignees?: TaskAssigneesInterface,
    evaluationScoreListOptions?: EvaluationScoreListOptionsInterface
  ): Observable<CreateEvaluationTaskResponseInterface>;

  createEvaluationTaskForWizard(
    task: TaskInterface,
    assignees: TaskAssigneeIdsInterface,
    evaluationConfig: CreateEvaluationTaskConfigInterface
  ): Observable<CreateEvaluationTaskResponseInterface>;

  createEvaluationAndAdaptiveTask(
    task: TaskInterface,
    assignees: TaskAssigneeIdsInterface,
    evaluationConfig: CreateEvaluationTaskConfigInterface
  ): Observable<CreateEvaluationAndAdaptiveTaskResponseInterface>;

  deleteEvaluationTasks(taskIds: number[]): Observable<DeleteEvaluationTasksResultInterface>;
  deleteTasks(taskIds: number[]): Observable<UpdateTaskResultInterface>;

  updateAccess(
    userId: number,
    taskId: number,
    taskGroups: TaskGroupInterface[],
    taskStudents: TaskStudentInterface[],
    taskClassGroups?: TaskClassGroupInterface[]
  ): Observable<TaskInterface>;
  printTask(taskId: number, withNames: boolean, size: PrintSize);
  printSolution(taskId: number, size: PrintSize);
  startEvaluationTask(taskId: number, studentIds?: number[]): Observable<StartEvaluationTaskResultInterface>;
  stopEvaluationTask(taskId: number): Observable<StopEvaluationTaskResultInterface>;
  updateEvaluationScoreListSubjects(
    taskId: number,
    evaluationSubjectIds: number[]
  ): Observable<UpdateEvaluationScoreListSubjectResponseInterface>;
  createAdaptiveTaskFromEvaluationTask(evaluationTaskId: number): Observable<CreateAdaptiveTaskResponseInterface>;
}

export interface TaskActiveErrorInterface {
  task: string;
  activeUntil: Date;
  user: string;
}

export interface UpdateTaskResultInterface extends BulkUpdateResultInfoInterface<
  TaskInterface,
  TaskActiveErrorInterface
> {}

export interface StartEvaluationTaskResultInterface extends FeedbackTriggeringAction {
  task: TaskInterface;
  taskInstances: TaskInstanceInterface[];
}

export interface StopEvaluationTaskResultInterface extends FeedbackTriggeringAction {
  taskInstances: TaskInstanceInterface[];
}

export interface DeleteEvaluationTasksResultInterface {
  success: {
    tasks: number[];
    taskClassGroups: number[];
    taskEduContents: number[];
    evaluationScoreListSubjects: number[];
    evaluationScoreLists: number[];
    evaluationScores: number[];
    results: number[];
  };
  errors: { id: number; reason: string; task: TaskInterface; relatedTasks: TaskInterface[] }[];
}
